import type { PersistedClient, Persister } from "@tanstack/react-query-persist-client";
import { get, set, del } from "idb-keyval";

const IDB_KEY = "ona-query-cache";

/**
 * Wipe the persisted cache without needing a Persister instance.
 *
 * Call this before reloading after an unhandled error so the next page load
 * starts with a clean cache rather than restoring the data that caused the crash.
 */
export async function clearPersistedCache(): Promise<void> {
    try {
        await del(IDB_KEY);
    } catch (error) {
        console.warn("Failed to clear persisted query cache:", error);
    }
}

/**
 * Bump this when proto message schemas change in a breaking way
 * (field removals, type changes, renamed fields) that would make
 * persisted query data incompatible with the current dashboard code.
 *
 * Routine deploys do NOT require a bump — only structural data shape changes.
 */
export const QUERY_CACHE_VERSION = "1";

/**
 * How long to wait after the last cache event before writing to IndexedDB.
 * Prevents a write storm when many queries settle in quick succession
 * (e.g. initial page load, polling bursts, bulk invalidations).
 */
export const PERSIST_THROTTLE_MS = 2_000;

/**
 * IndexedDB-backed persister for TanStack Query.
 *
 * Uses idb-keyval which stores via the structured clone algorithm,
 * avoiding JSON serialization entirely. This is important because
 * protobuf-es v2 messages contain BigInt values (e.g., Timestamp.seconds)
 * that JSON.stringify cannot handle.
 *
 * Writes are throttled: after a persistClient call, subsequent calls
 * within PERSIST_THROTTLE_MS are coalesced into a single trailing write.
 * This avoids blocking the main thread with repeated structured-clone
 * serialization of the full query cache on every cache event.
 */
export function createIDBPersister(): Persister {
    let pendingClient: PersistedClient | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const flush = async () => {
        timer = undefined;
        const client = pendingClient;
        pendingClient = undefined;
        if (!client) return;
        try {
            await set(IDB_KEY, client);
        } catch (error) {
            console.warn("Failed to persist query cache:", error);
        }
    };

    return {
        persistClient: (client: PersistedClient) => {
            pendingClient = client;
            if (timer === undefined) {
                timer = setTimeout(flush, PERSIST_THROTTLE_MS);
            }
        },
        restoreClient: async () => {
            try {
                return await get<PersistedClient>(IDB_KEY);
            } catch (error) {
                console.warn("Failed to restore query cache:", error);
                return undefined;
            }
        },
        removeClient: async () => {
            // Cancel any pending write — the cache is being discarded.
            pendingClient = undefined;
            if (timer !== undefined) {
                clearTimeout(timer);
                timer = undefined;
            }
            try {
                await del(IDB_KEY);
            } catch (error) {
                console.warn("Failed to remove query cache:", error);
            }
        },
    };
}
